import { useCallback, useEffect, useRef, useState } from 'react'
import {
  MULTIPLAYER_UI,
  ROOM_CAPACITY,
  ROOM_POLL_INTERVAL_MS,
  type InviteCode,
  type RoomInfo,
} from '../config/multiplayer'
import {
  createInvite,
  createRoom,
  getRoomInfo,
  joinByInvite,
  leaveRoom,
} from '../services/rooms'

type Tab = 'host' | 'guest'

interface Props {
  open: boolean
  onClose: () => void
  onRoomReady: (room: RoomInfo) => void
}

/**
 * フレンドマッチ（あいことば）モーダル。
 *
 * - ホストタブ: createRoom → createInvite → 待機（`getRoomInfo` をポーリングして
 *   相手の参加を検知し、ルーム定員到達時に onRoomReady を呼ぶ）
 * - ゲストタブ: 入力したコードで joinByInvite → 成功で onRoomReady
 *
 * キャンセル時はホスト側で生成済みルームを leaveRoom してから閉じる（不要なルームが
 * TTL 30 分残留するのを避ける）。
 *
 * Line Mini App 拡張ポイント（T16 で詰める）:
 *   - hostShare() で `liff.shareTargetPicker([...])` を呼び分ける想定。
 *     現状は navigator.share / clipboard にフォールバックする。
 */
export function FriendMatchModal({ open, onClose, onRoomReady }: Props) {
  const [tab, setTab] = useState<Tab>('host')
  const [hostRoom, setHostRoom] = useState<RoomInfo | null>(null)
  const [invite, setInvite] = useState<InviteCode | null>(null)
  const [hostBusy, setHostBusy] = useState(false)
  const [hostError, setHostError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [codeInput, setCodeInput] = useState('')
  const [guestBusy, setGuestBusy] = useState(false)
  const [guestError, setGuestError] = useState<string | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const currentRoomIdRef = useRef<string | null>(null)

  // モーダル共通
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // 閉じたらローカル状態リセット
  useEffect(() => {
    if (open) return
    setTab('host')
    setHostRoom(null)
    setInvite(null)
    setHostBusy(false)
    setHostError(null)
    setCopied(false)
    setCodeInput('')
    setGuestBusy(false)
    setGuestError(null)
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
    currentRoomIdRef.current = null
  }, [open])

  // ホストルームができたら getRoomInfo をポーリングして参加検知。
  // 依存配列は roomId のみとし、setHostRoom（ポーリング tick 内で発火）による
  // useEffect 再起動 → onRoomReady 二重発火を防ぐ。onRoomReady と「定員到達による
  // 即時通知」は ref 経由で参照する。
  const onRoomReadyRef = useRef(onRoomReady)
  useEffect(() => {
    onRoomReadyRef.current = onRoomReady
  }, [onRoomReady])

  const hostRoomId = hostRoom?.roomId ?? null
  const hostRoomCapacity = hostRoom?.capacity ?? null
  const hostRoomMembersAtMount = hostRoom?.members.length ?? 0
  useEffect(() => {
    if (!open || !hostRoomId || hostRoomCapacity === null) return
    // 既に定員到達なら即通知（createRoom 直後は通常 1 人なので発火しない）
    if (hostRoomMembersAtMount >= hostRoomCapacity) {
      const current = hostRoom
      if (current) onRoomReadyRef.current(current)
      return
    }
    let firedReady = false
    currentRoomIdRef.current = hostRoomId
    const tick = async () => {
      if (firedReady) return
      try {
        const latest = await getRoomInfo(hostRoomId)
        if (latest.members.length >= latest.capacity) {
          firedReady = true
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current)
            pollTimerRef.current = null
          }
          onRoomReadyRef.current(latest)
          return
        }
        // 定員未到達のときのみ表示更新する。定員到達後は onRoomReady が画面遷移を
        // 引き起こすため、setHostRoom を呼んで本 useEffect を再起動させない。
        setHostRoom(latest)
      } catch (err) {
        // 410 Gone など明確にルーム終了した場合はエラー表示しポーリング停止
        const status = (err as { status?: number } | null)?.status
        if (status === 410 || status === 404) {
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current)
            pollTimerRef.current = null
          }
          setHostError('ルームの有効期限が切れました。もう一度作り直してください。')
        } else {
          console.warn('[FriendMatch] room poll failed:', err)
        }
      }
    }
    pollTimerRef.current = setInterval(() => {
      void tick()
    }, ROOM_POLL_INTERVAL_MS)
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hostRoomId, hostRoomCapacity])

  const cleanupHostRoom = useCallback(async () => {
    const roomId = currentRoomIdRef.current
    currentRoomIdRef.current = null
    if (!roomId) return
    try {
      await leaveRoom(roomId)
    } catch (err) {
      console.warn('[FriendMatch] leaveRoom failed:', err)
    }
  }, [])

  const handleHostCreate = async () => {
    setHostBusy(true)
    setHostError(null)
    setCopied(false)
    try {
      const room = await createRoom({ capacity: ROOM_CAPACITY })
      setHostRoom(room)
      const code = await createInvite(room.roomId)
      setInvite(code)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setHostError(message)
      // createRoom は成功していて createInvite が失敗した場合、ルームを掃除する
      await cleanupHostRoom()
    } finally {
      setHostBusy(false)
    }
  }

  const handleCopy = async () => {
    if (!invite) return
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(invite.code)
      } else {
        // fallback:ok — clipboard 非対応環境では textarea hack にフォールバック
        const ta = document.createElement('textarea')
        ta.value = invite.code
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.warn('[FriendMatch] copy failed:', err)
    }
  }

  const handleShare = async () => {
    if (!invite) return
    const shareText = `ラッキーどうぶつナワバリで対戦しよう！\nあいことば: ${invite.code}`
    // LIFF 環境向け: T16 で liff.shareTargetPicker に差し替える。
    // それ以外では Web Share API にフォールバック。
    const w = window as unknown as {
      liff?: { shareTargetPicker?: (messages: unknown[]) => Promise<unknown> }
    }
    if (w.liff?.shareTargetPicker) {
      try {
        await w.liff.shareTargetPicker([{ type: 'text', text: shareText }])
        return
      } catch (err) {
        console.warn('[FriendMatch] liff.shareTargetPicker failed:', err)
      }
    }
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ text: shareText })
        return
      } catch (err) {
        // キャンセルはエラーにしない
        if ((err as Error)?.name !== 'AbortError') {
          console.warn('[FriendMatch] navigator.share failed:', err)
        }
      }
    }
    // 最終フォールバック: コピー動作に任せる
    await handleCopy()
  }

  const handleHostCancel = async () => {
    await cleanupHostRoom()
    onClose()
  }

  const handleGuestJoin = async () => {
    if (!codeInput.trim()) return
    setGuestBusy(true)
    setGuestError(null)
    try {
      const { room } = await joinByInvite(codeInput.trim())
      onRoomReady(room)
    } catch (err) {
      const status = (err as { status?: number } | null)?.status
      if (status === 404 || status === 409 || status === 410) {
        setGuestError(MULTIPLAYER_UI.friendJoinError)
      } else {
        const message = err instanceof Error ? err.message : String(err)
        setGuestError(message)
      }
    } finally {
      setGuestBusy(false)
    }
  }

  const handleClose = async () => {
    if (hostRoom) {
      await cleanupHostRoom()
    }
    onClose()
  }

  if (!open) return null

  return (
    <div
      className="auth-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fm-modal-title"
    >
      <div className="auth-modal__dialog" onClick={(e) => e.stopPropagation()}>
        <header className="auth-modal__header">
          <h2 id="fm-modal-title" className="auth-modal__title">
            あいことば対戦
          </h2>
          <button
            type="button"
            className="auth-modal__close"
            aria-label="閉じる"
            disabled={hostBusy || guestBusy}
            onClick={() => {
              void handleClose()
            }}
          >
            ×
          </button>
        </header>

        <div className="auth-modal__body">
          {/* タブ */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className={tab === 'host' ? 'btn btn--primary' : 'btn btn--secondary'}
              style={{ flex: 1 }}
              onClick={() => setTab('host')}
              disabled={guestBusy || !!hostRoom}
            >
              {MULTIPLAYER_UI.friendHostTabLabel}
            </button>
            <button
              type="button"
              className={tab === 'guest' ? 'btn btn--primary' : 'btn btn--secondary'}
              style={{ flex: 1 }}
              onClick={() => setTab('guest')}
              disabled={hostBusy || !!hostRoom}
            >
              {MULTIPLAYER_UI.friendGuestTabLabel}
            </button>
          </div>

          {/* ホストタブ */}
          {tab === 'host' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {!invite && !hostRoom && (
                <button
                  type="button"
                  className="btn btn--primary btn--large"
                  onClick={() => {
                    void handleHostCreate()
                  }}
                  disabled={hostBusy}
                >
                  {hostBusy ? '作成中…' : MULTIPLAYER_UI.friendCreateButton}
                </button>
              )}

              {invite && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: 16,
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: 12,
                  }}
                >
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                    あいことば
                  </p>
                  <p
                    style={{
                      fontSize: 28,
                      fontWeight: 900,
                      color: '#ffe066',
                      letterSpacing: '0.1em',
                      margin: '8px 0',
                    }}
                  >
                    {invite.code}
                  </p>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => {
                        void handleCopy()
                      }}
                    >
                      {copied ? MULTIPLAYER_UI.friendCopied : MULTIPLAYER_UI.friendCopyButton}
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => {
                        void handleShare()
                      }}
                    >
                      {MULTIPLAYER_UI.friendShareButton}
                    </button>
                  </div>
                  <p
                    style={{
                      marginTop: 12,
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.7)',
                    }}
                  >
                    {MULTIPLAYER_UI.friendCreateWaiting}
                  </p>
                </div>
              )}

              {hostError && (
                <div className="auth-modal__error" role="alert">
                  <p>{MULTIPLAYER_UI.friendError}</p>
                  <p className="auth-modal__error-detail">{hostError}</p>
                </div>
              )}

              {(invite || hostRoom) && (
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => {
                    void handleHostCancel()
                  }}
                >
                  {MULTIPLAYER_UI.friendCancelButton}
                </button>
              )}
            </div>
          )}

          {/* ゲストタブ */}
          {tab === 'guest' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="text"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                placeholder={MULTIPLAYER_UI.friendCodePlaceholder}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: 18,
                  fontWeight: 700,
                  textAlign: 'center',
                  letterSpacing: '0.1em',
                  borderRadius: 12,
                  border: '2px solid rgba(255,255,255,0.25)',
                  background: 'rgba(0,0,0,0.35)',
                  color: '#fff',
                }}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                disabled={guestBusy}
              />
              <button
                type="button"
                className="btn btn--primary btn--large"
                disabled={guestBusy || !codeInput.trim()}
                onClick={() => {
                  void handleGuestJoin()
                }}
              >
                {guestBusy ? '参加中…' : MULTIPLAYER_UI.friendJoinButton}
              </button>
              {guestError && (
                <div className="auth-modal__error" role="alert">
                  <p>{guestError}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
